/** packages/shared-components/src/components/Keyboard/Keyboard.tsx */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { KeyboardProps, KeyState, KeyConfig, KeyPosition } from "./types";
import { Key } from "./Key";
import {
  generateDefaultLayout,
  applyX75v3StaticWidthsToLayout,
} from "../../utils/keyboardLayoutUtils";
import { rgbToHex, applyBrightness, isGap } from "./utils";
import {
  lightingMatrixKey,
  lightingMatrixKeyUpper,
  lightingMatrixKeyLower,
  resolveEs68FamilyLightingGridCol,
  isDisplayableLightingCell,
} from "../../utils/lightingUtils";
import type { KeyLightingConfig } from "./types";
import { KEYCODE_TO_WEB_KEY_MAP } from "../../utils/keyCodeMapper";
import {
  resolveLayoutDataRowIndex,
  resolveSdkMatrixRowFromVisual,
  resolveCalibrationDataColIndex,
  resolvePerformanceSdkColIndex,
  resolveKeyPositionMapRow,
  usesSliceFirstRowLayout,
  isX75v3KeyboardType,
  resolveGamma75CalibrationSampleCol,
} from "../../utils/sliceFirstRowKeyboardMatrix";
import { resolveEs87CustomLightGridCell } from "../../utils/es87LightingUtils";
import { isTank68HeCalibrationRouteActive } from "@eweadn-qiankun/shared-utils";
import { resolveX87HeCustomLightGridCell } from "../../utils/x87HeLightingUtils";
import { resolveAdvancedKeyModeForKey } from "../../utils/advancedKeyLayoutUtils";
import { KeyboardSkeleton } from "./KeyboardSkeleton";
import { ViewportFit } from "./ViewportFit";
import "./Keyboard.css";

const isWindowDragging = () =>
  typeof window !== "undefined" && (window as any).__electronWindowDragging;

function isEditableKeyboardEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return !target.disabled && !target.readOnly;
  }
  return Boolean(target.closest('[contenteditable="true"]'));
}

/** Windows 右 Alt 常上报 AltGraph，与布局 code AltRight 对齐 */
function normalizePhysicalKeyEventCode(code: string): string {
  if (code === "AltGraph") return "AltRight";
  return code;
}

/** 不拦截输入框/文本域及常见编辑快捷键（Ctrl/Cmd + V/C/X/A/Z/Y） */
function shouldIgnorePhysicalKeyboardEvent(e: KeyboardEvent): boolean {
  if (isEditableKeyboardEventTarget(e.target)) return true;
  if (!e.ctrlKey && !e.metaKey) return false;
  const key = e.key.toLowerCase();
  return ["v", "c", "x", "a", "z", "y"].includes(key);
}

/**
 * neutral 下 `.shared-keyboard-key` 1px 边框、`.shared-keyboard` padding、行 margin 等与纯几何推导的差值（实测补全）。
 */
const NEUTRAL_INTRINSIC_SLACK = { width: 12, height: 16 } as const;

/**
 * 按键为 position:absolute 时，行内无流式子级，row.scrollWidth 在部分环境下不可靠，
 * 会导致 .shared-keyboard 的 style.width 被误测、逐次变小。按与 renderKey 相同的累加规则算理论边界。
 */
function computeIntrinsicKeyboardBounds(
  layout: KeyConfig[][],
  keySize: number,
  keyGap: number,
  containerPadding: number,
  slack: { width: number; height: number } = { width: 0, height: 0 }
): { width: number; height: number } {
  if (!layout?.length) return { width: 0, height: 0 };
  let maxRight = containerPadding;
  let maxBottom = containerPadding;

  layout.forEach((row, rowIndex) => {
    let cursorX = containerPadding;
    for (let i = 0; i < row.length; i++) {
      const k = row[i];
      if (isGap(k)) {
        cursorX += (Number(k.multipleWidth) || 0) * keySize;
        continue;
      }
      const mw = Number(k.multipleWidth) || 1;
      const w = mw * keySize;
      maxRight = Math.max(maxRight, cursorX + w);
      cursorX += mw * keySize + keyGap;

      const keyH =
        (k.verticalSpan ?? ([87, 88].includes(k.keyCode) ? 2 : 1)) > 1
          ? 2.1 * keySize
          : keySize;
      const topY = rowIndex * (keySize + keyGap) + containerPadding;
      maxBottom = Math.max(maxBottom, topY + keyH);
    }
  });

  return {
    width: Math.ceil(maxRight + containerPadding) + slack.width,
    height: Math.ceil(maxBottom + containerPadding) + slack.height,
  };
}

// 检查两个矩形是否相交
const isRectIntersection = (rect1: DOMRect, rect2: DOMRect): boolean => {
  return (
    rect1.left < rect2.right &&
    rect1.right > rect2.left &&
    rect1.top < rect2.bottom &&
    rect1.bottom > rect2.top
  );
};

export const Keyboard: React.FC<KeyboardProps> = ({
  keyboardType = "87",
  layout,
  selectionMode = "single", // 用于判断是否可框选
  enablePhysicalKeyboard = true,
  selectedKeys,
  onKeySelect,
  onPhysicalKeyPress,
  onPhysicalKeyRelease,
  lightingMode = "default",
  styleMode = "colorful",
  keyColors,
  brightness = 100,
  showBrightness = true,
  onKeyClick,
  onKeyContextMenu,
  onKeyDoubleClick,
  onKeyDrop,
  disabled = false,
  disabledKeyCodes,
  remappingMode = false,
  draggable = false,
  isExtraKeys = false,
  extraKeyTooltipFormat = "comm",
  className,
  showLabels = true,
  keySize = 54,
  keyGap = 4,
  advancedKeyModes,
  pressedKeyStyle,
  selectedKeyStyle,
  selectedKeyBadge,
  selectedKeyTooltipText,
  selectedKeyStyleMap,
  selectedKeysKeepRealtimeColor = [],
  customColorCornerIconSrc,
  customColorCornerIconUuids,
  randomValues = [],
  showRandomValues = false,
  hideCalibrationMetrics = false,
  onGetPerformance,
  showTeampreset = false,
  teampresetData = [],
  showAxis = false,
  axisData,
  axisLinkHoverId = null,
  resolveAxisLinkIdFromData,
  axisDataMatchesAxisLink,
  onAxisLinkHoverChange,
  onReady,
  layoutLoading = false,
  disableViewportFit = false,
  allowIconKeySelectedStyle = false,
  suppressSelectedKeyStyle = false,
  selectionHostRef,
  selectionHostIgnoreSelector,
  doubleLightingKeySplit = false,
  doubleLightingSplitLayer = "dual",
}) => {
  // 计算当前键盘布局
  const activeLayout = useMemo(() => {
    let computedLayout = layout || generateDefaultLayout(keyboardType);
    if (usesSliceFirstRowLayout(keyboardType)) {
      computedLayout = computedLayout.slice(1);
    }
    if (isX75v3KeyboardType(keyboardType)) {
      computedLayout = applyX75v3StaticWidthsToLayout(
        computedLayout,
        keyboardType
      );
    }
    return computedLayout;
  }, [layout, keyboardType]);

  const containerPadding = 6;

  /** x75v3：宽度已由静态表同步，直接展示 */
  const displayLayout = activeLayout;

  const stableSelectedKeys = selectedKeys || [];

  // 存储按键状态
  const [keyStates, setKeyStates] = useState<Record<string, KeyState>>({});
  // 内部选中的按键列表
  const [internalSelected, setInternalSelected] =
    useState<string[]>(stableSelectedKeys);
  const internalSelectedRef = useRef<string[]>(stableSelectedKeys);
  // 键盘的 DOM 引用
  const keyboardRef = useRef<HTMLDivElement>(null);
  /** 框选区域父级：与页面键盘展示区同大，虚线框相对此容器定位 */
  const selectionRootRef = useRef<HTMLDivElement>(null);
  // 按键的 DOM 引用映射
  const keyRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // 键盘尺寸
  const [keyboardDimensions, setKeyboardDimensions] = useState({
    width: 0,
    height: 0,
  });

  // 使用 useRef 保持回调函数的引用稳定
  const onPhysicalKeyPressRef = useRef(onPhysicalKeyPress);
  const onPhysicalKeyReleaseRef = useRef(onPhysicalKeyRelease);
  const onKeySelectRef = useRef(onKeySelect);
  const onKeyClickRef = useRef(onKeyClick);
  const onKeyContextMenuRef = useRef(onKeyContextMenu);
  const onKeyDoubleClickRef = useRef(onKeyDoubleClick);
  const onKeyDropRef = useRef(onKeyDrop);
  const onReadyRef = useRef(onReady);

  // 框选状态
  const [isSelecting, setIsSelecting] = useState(false);
  // 框选起始位置
  const [selectionStartPos, setSelectionStartPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // 框选当前位置
  const [selectionCurrentPos, setSelectionCurrentPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // 长按定时器，用于区分点击和拖拽
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false);
  const selectionStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const keyMouseDownRef = useRef(false);
  const isLeftMouseDownRef = useRef(false);
  const dragSelectStartKeyRef = useRef<string | null>(null);
  /** 滑动选中起始键是否已选中：已选中则滑动取消，未选中则滑动选中 */
  const dragSelectModeRef = useRef<"add" | "remove">("add");
  const suppressClickAfterSlideRef = useRef(false);
  const suppressClickResetTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const resolveLightingGridCol = useCallback(
    (key: KeyConfig, layoutRowIndex: number): number | undefined => {
      if (key.originalCol === undefined) return undefined;
      const matrixRow =
        key.originalRow !== undefined ? key.originalRow : layoutRowIndex;
      if (keyboardType === "es87" && key.keyCode) {
        return resolveEs87CustomLightGridCell(
          matrixRow,
          key.keyCode,
          key.originalCol
        ).col;
      }
      if (keyboardType === "87") {
        return resolveX87HeCustomLightGridCell(
          matrixRow,
          key.keyCode ?? 0,
          key.originalCol
        ).col;
      }
      if (usesSliceFirstRowLayout(keyboardType)) {
        return resolveEs68FamilyLightingGridCol(matrixRow, key.originalCol);
      }
      return key.originalCol;
    },
    [keyboardType]
  );

  // 按键位置映射：slice/x75v3 等用固件 originalRow；其余用 layout 行
  const keyPositionMap = useMemo(() => {
    const positionMap: Record<string, KeyPosition> = {};
    activeLayout.forEach((row, rowIndex) => {
      row.forEach((key) => {
        if (
          isGap(key) ||
          key.originalCol === undefined ||
          key.originalCol < 0
        ) {
          return;
        }
        positionMap[key.uuid] = {
          row: resolveKeyPositionMapRow(key, rowIndex, keyboardType),
          col:
            remappingMode || !key.keyCode
              ? key.originalCol
              : keyboardType === "es87"
                ? resolveEs87CustomLightGridCell(
                    key.originalRow ?? rowIndex,
                    key.keyCode,
                    key.originalCol
                  ).col
                : key.originalCol,
          keyCode: key.keyCode,
          code: key.code,
          label: key.label || "",
          uuid: key.uuid || "",
        };
      });
    });
    return positionMap;
  }, [activeLayout, keyboardType, remappingMode]);

  const intrinsicKeyboardBounds = useMemo(
    () =>
      computeIntrinsicKeyboardBounds(
        displayLayout,
        keySize,
        keyGap,
        containerPadding,
        styleMode === "neutral"
          ? NEUTRAL_INTRINSIC_SLACK
          : { width: 0, height: 0 }
      ),
    [displayLayout, keySize, keyGap, containerPadding, styleMode]
  );

  // 有矩阵时仅用 layout 推导尺寸（与 absolute 键位一致），勿与 scroll 测量取 max：改键后文案变长会抬高 scrollHeight / max-content，造成每次 +8px 类膨胀
  useLayoutEffect(() => {
    if (layoutLoading) return;

    const iw = intrinsicKeyboardBounds.width;
    const ih = intrinsicKeyboardBounds.height;
    if (iw > 0 && ih > 0) {
      setKeyboardDimensions({ width: iw, height: ih });
      return;
    }

    if (!keyboardRef.current) return;

    const measureKeyboard = () => {
      if (!keyboardRef.current) return;

      const rows = keyboardRef.current.querySelectorAll(".shared-keyboard-row");
      let maxWidth = 0;

      rows.forEach((row) => {
        const rowWidth = row.scrollWidth;
        maxWidth = Math.max(maxWidth, rowWidth);
      });

      const actualHeight = keyboardRef.current.scrollHeight;
      const pad = containerPadding * 2;

      setKeyboardDimensions({
        width: Math.ceil(maxWidth + pad),
        height: Math.ceil(actualHeight + pad),
      });
    };

    measureKeyboard();
    let raf = 0;
    raf = requestAnimationFrame(() => {
      measureKeyboard();
    });
    return () => cancelAnimationFrame(raf);
  }, [
    intrinsicKeyboardBounds.width,
    intrinsicKeyboardBounds.height,
    keySize,
    keyGap,
    containerPadding,
    layoutLoading,
    styleMode,
  ]);

  // 布局渲染完成 + 尺寸测量完成后，通知外部（骨架屏阶段不触发）
  useEffect(() => {
    if (
      layoutLoading ||
      activeLayout.length === 0 ||
      keyboardDimensions.width === 0 ||
      keyboardDimensions.height === 0
    ) {
      return;
    }
    onReadyRef.current?.();
  }, [
    layoutLoading,
    activeLayout,
    keyboardDimensions.width,
    keyboardDimensions.height,
  ]);

  // 更新回调函数的引用
  useEffect(() => {
    onPhysicalKeyPressRef.current = onPhysicalKeyPress;
    onPhysicalKeyReleaseRef.current = onPhysicalKeyRelease;
    onKeySelectRef.current = onKeySelect;
    onKeyClickRef.current = onKeyClick;
    onKeyContextMenuRef.current = onKeyContextMenu;
    onKeyDoubleClickRef.current = onKeyDoubleClick;
    onKeyDropRef.current = onKeyDrop;
    onReadyRef.current = onReady;
  });

  // 同步外部传入的选中按键
  useEffect(() => {
    if (selectedKeys !== undefined) {
      setInternalSelected(selectedKeys);
      internalSelectedRef.current = selectedKeys;
    }
  }, [selectedKeys]);

  // 校准结束或重新开始时清空物理按压态，避免残留橙色高亮
  useEffect(() => {
    if (!showRandomValues) {
      setKeyStates({});
    }
  }, [showRandomValues]);

  useEffect(() => {
    internalSelectedRef.current = internalSelected;
  }, [internalSelected]);

  useEffect(() => {
    return () => {
      if (suppressClickResetTimerRef.current) {
        clearTimeout(suppressClickResetTimerRef.current);
        suppressClickResetTimerRef.current = null;
      }
    };
  }, []);

  const emitSelectionChange = useCallback(
    (selected: string[]) => {
      const selectedPositions: Record<string, KeyPosition> = {};
      selected.forEach((code) => {
        if (keyPositionMap[code]) {
          selectedPositions[code] = keyPositionMap[code];
        }
      });
      onKeySelectRef.current?.(selected, selectedPositions);
    },
    [keyPositionMap]
  );

  // 物理键盘事件处理
  useEffect(() => {
    if (!enablePhysicalKeyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldIgnorePhysicalKeyboardEvent(e)) return;
      e.preventDefault();
      const code = normalizePhysicalKeyEventCode(e.code);

      setKeyStates((prev) => ({
        ...prev,
        [code]: { ...prev[code], pressed: true },
      }));

      onPhysicalKeyPressRef.current?.(code);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (shouldIgnorePhysicalKeyboardEvent(e)) return;
      e.preventDefault();
      const code = normalizePhysicalKeyEventCode(e.code);

      setKeyStates((prev) => ({
        ...prev,
        [code]: { ...prev[code], pressed: false },
      }));

      onPhysicalKeyReleaseRef.current?.(code);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [enablePhysicalKeyboard]);

  // 按键点击事件处理
  const handleKeyClick = useCallback(
    (uuid: string, config: KeyConfig, e: React.MouseEvent) => {
      if (isSelecting) return;
      if (suppressClickAfterSlideRef.current) {
        suppressClickAfterSlideRef.current = false;
        return;
      }
      if (e.detail >= 2) {
        return;
      }

      console.log(uuid, config, "------------------");
      onKeyClickRef.current?.(uuid, e);
      let newSelected: string[];
      const currentSelected = internalSelectedRef.current;

      if (selectionMode === "multiple") {
        if (currentSelected.includes(uuid)) {
          newSelected = currentSelected.filter((k) => k !== uuid);
        } else {
          newSelected = [...currentSelected, uuid];
        }
      } else {
        newSelected = currentSelected.includes(uuid) ? [] : [uuid];
      }

      setInternalSelected(newSelected);
      internalSelectedRef.current = newSelected;
      emitSelectionChange(newSelected);
    },
    [selectionMode, isSelecting, emitSelectionChange]
  );

  const handleKeyMouseDownSelect = useCallback(
    (uuid: string, _config: KeyConfig, e: React.MouseEvent) => {
      if (isWindowDragging()) return;
      if (disabled || draggable || selectionMode !== "multiple") return;
      if (e.button !== 0) return;
      keyMouseDownRef.current = true;
      isLeftMouseDownRef.current = true;
      dragSelectStartKeyRef.current = uuid;
      dragSelectModeRef.current = internalSelectedRef.current.includes(uuid)
        ? "remove"
        : "add";
      suppressClickAfterSlideRef.current = false;
    },
    [disabled, draggable, selectionMode]
  );

  const handleKeyMouseEnterSelect = useCallback(
    (uuid: string, _config: KeyConfig, e: React.MouseEvent) => {
      if (isWindowDragging()) return;
      if (disabled || draggable || selectionMode !== "multiple") return;
      // 仅以真实按下左键为准，避免 isLeftMouseDownRef 未复位时 hover 误选
      if (e.buttons !== 1) return;

      const startUuid = dragSelectStartKeyRef.current;
      const keysToApply = [startUuid, uuid].filter(
        (item): item is string => typeof item === "string"
      );
      if (!keysToApply.length) return;

      const mode = dragSelectModeRef.current;
      const current = internalSelectedRef.current;
      let next = [...current];
      let changed = false;

      if (mode === "add") {
        keysToApply.forEach((item) => {
          if (!next.includes(item)) {
            next.push(item);
            changed = true;
          }
        });
      } else {
        const removeSet = new Set(keysToApply);
        const filtered = next.filter((k) => !removeSet.has(k));
        if (filtered.length !== next.length) {
          next = filtered;
          changed = true;
        }
      }

      if (!changed) return;

      setInternalSelected(next);
      internalSelectedRef.current = next;
      emitSelectionChange(next);

      if (startUuid && uuid !== startUuid) {
        suppressClickAfterSlideRef.current = true;
      }
    },
    [disabled, draggable, selectionMode, emitSelectionChange]
  );

  // 按键拖放事件处理
  const handleKeyDrop = useCallback(
    (sourceKeyCode: number, targetRow: number, targetCol: number) => {
      onKeyDropRef.current?.(sourceKeyCode, targetRow, targetCol);
    },
    []
  );

  const handleKeyContextMenu = useCallback(
    (uuid: string, _config: KeyConfig, e: React.MouseEvent) => {
      if (disabled) return;
      const pos = keyPositionMap[uuid];
      if (!pos) return;
      onKeyContextMenuRef.current?.(uuid, pos, e);
    },
    [disabled, keyPositionMap]
  );

  const handleKeyDoubleClick = useCallback(
    (uuid: string, _config: KeyConfig, e: React.MouseEvent) => {
      if (disabled) return;
      const pos = keyPositionMap[uuid];
      if (!pos) return;
      onKeyDoubleClickRef.current?.(uuid, pos, e);
    },
    [disabled, keyPositionMap]
  );

  const formatKeyLightingCellColor = (
    cell: KeyLightingConfig | undefined
  ): string | undefined => {
    if (!isDisplayableLightingCell(cell)) return undefined;
    if (showBrightness) {
      return applyBrightness(cell, brightness);
    }
    return rgbToHex(cell.R, cell.G, cell.B);
  };

  const resolveMatrixLayerColor = (
    key: KeyConfig,
    layoutRowIndex: number,
    matrixKeyFn: (row: number, col: number) => string
  ): string | undefined => {
    if (lightingMode !== "custom" || !keyColors) return undefined;
    const matrixRow =
      key.originalRow !== undefined ? key.originalRow : layoutRowIndex;
    const lightingCol = resolveLightingGridCol(key, layoutRowIndex);
    if (lightingCol === undefined) return undefined;
    return formatKeyLightingCellColor(
      keyColors[matrixKeyFn(matrixRow, lightingCol)]
    );
  };

  // 获取按键颜色
  const getKeyColor = (
    key: KeyConfig,
    layoutRowIndex: number
  ): string | undefined => {
    if (lightingMode === "off") {
      return undefined;
    }

    if (lightingMode === "custom" && keyColors) {
      // 自定义灯优先按矩阵 (originalRow, originalCol) 取色，避免 Web code 与固件矩阵错位
      const matrixRow =
        key.originalRow !== undefined ? key.originalRow : layoutRowIndex;
      const lightingCol = resolveLightingGridCol(key, layoutRowIndex);
      const matrixColor =
        lightingCol !== undefined
          ? keyColors[lightingMatrixKey(matrixRow, lightingCol)]
          : undefined;
      const codeColor = key.code ? keyColors[key.code] : undefined;
      const webKeyFromKeyCode =
        key.keyCode != null && Number.isFinite(Number(key.keyCode))
          ? KEYCODE_TO_WEB_KEY_MAP[Number(key.keyCode)]
          : undefined;
      const keyCodeColor = webKeyFromKeyCode
        ? keyColors[webKeyFromKeyCode]
        : undefined;
      /** 矩阵占位 OFF 时须回退 code，避免律动/自定义 code 色被挡住 */
      const color = [matrixColor, codeColor, keyCodeColor].find((cell) =>
        isDisplayableLightingCell(cell)
      );
      return formatKeyLightingCellColor(color);
    }

    return undefined;
  };

  /** 双灯位：分别取上下灯矩阵色；上/下灯位 Tab 仅对应半区填色 */
  const getKeySplitColors = (
    key: KeyConfig,
    layoutRowIndex: number
  ): { top?: string; bottom?: string } | undefined => {
    if (!doubleLightingKeySplit || lightingMode !== "custom") return undefined;

    const readTop = () =>
      resolveMatrixLayerColor(key, layoutRowIndex, lightingMatrixKeyUpper) ??
      (doubleLightingSplitLayer === "upper"
        ? getKeyColor(key, layoutRowIndex)
        : undefined);

    const readBottom = () =>
      resolveMatrixLayerColor(key, layoutRowIndex, lightingMatrixKeyLower) ??
      (doubleLightingSplitLayer === "lower"
        ? getKeyColor(key, layoutRowIndex)
        : undefined);

    if (doubleLightingSplitLayer === "upper") {
      return { top: readTop(), bottom: undefined };
    }
    if (doubleLightingSplitLayer === "lower") {
      return { top: undefined, bottom: readBottom() };
    }

    const top = readTop();
    const bottom = readBottom();
    if (!top && !bottom) return undefined;
    return { top, bottom };
  };

  // 鼠标框选：支持外部 selectionHostRef 扩大热区，拖拽阶段使用 document 监听
  const getSelectionSurface = useCallback(() => {
    return selectionHostRef?.current ?? selectionRootRef.current;
  }, [selectionHostRef]);

  const cancelMarqueeTracking = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    isSelectingRef.current = false;
    selectionStartPosRef.current = null;
    setIsSelecting(false);
    setSelectionStartPos(null);
    setSelectionCurrentPos(null);
  }, []);

  const collectKeysInRect = useCallback((selectionRect: DOMRect) => {
    const newSelected: string[] = [];
    keyRefs.current.forEach((keyElement, uuid) => {
      if (!keyElement) return;
      const keyRect = keyElement.getBoundingClientRect();
      if (isRectIntersection(selectionRect, keyRect)) {
        newSelected.push(uuid);
      }
    });
    return newSelected;
  }, []);

  const updateMarqueeFromClientPoint = useCallback(
    (clientX: number, clientY: number) => {
      const start = selectionStartPosRef.current;
      if (!start) return;

      setSelectionCurrentPos({ x: clientX, y: clientY });

      const selectionRect: DOMRect = {
        left: Math.min(start.x, clientX),
        top: Math.min(start.y, clientY),
        right: Math.max(start.x, clientX),
        bottom: Math.max(start.y, clientY),
        width: Math.abs(clientX - start.x),
        height: Math.abs(clientY - start.y),
        x: Math.min(start.x, clientX),
        y: Math.min(start.y, clientY),
        toJSON: () => ({}),
      };

      const newSelected = collectKeysInRect(selectionRect);
      setInternalSelected(newSelected);
      internalSelectedRef.current = newSelected;
    },
    [collectKeysInRect]
  );

  const finishMarquee = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (isSelectingRef.current && selectionMode === "multiple") {
      emitSelectionChange(internalSelectedRef.current);
    }

    isLeftMouseDownRef.current = false;
    dragSelectStartKeyRef.current = null;
    dragSelectModeRef.current = "add";
    if (suppressClickResetTimerRef.current) {
      clearTimeout(suppressClickResetTimerRef.current);
    }
    suppressClickResetTimerRef.current = setTimeout(() => {
      suppressClickAfterSlideRef.current = false;
    }, 0);

    isSelectingRef.current = false;
    selectionStartPosRef.current = null;
    setIsSelecting(false);
    setSelectionStartPos(null);
    setSelectionCurrentPos(null);
  }, [selectionMode, emitSelectionChange]);

  const selectionHostIgnoreSelectorRef = useRef(selectionHostIgnoreSelector);
  selectionHostIgnoreSelectorRef.current = selectionHostIgnoreSelector;

  const shouldIgnoreMarqueeStart = useCallback((target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    if (!el?.closest) return false;
    if (el.closest(".keyboard-button")) return true;
    if (el.closest("button, a, input, textarea, select, label")) return true;
    const customIgnore = selectionHostIgnoreSelectorRef.current?.trim();
    if (customIgnore && el.closest(customIgnore)) return true;
    return false;
  }, []);

  const tryStartMarquee = useCallback(
    (e: MouseEvent) => {
      if (isWindowDragging()) return;
      if (disabled) return;
      if (keyMouseDownRef.current) {
        keyMouseDownRef.current = false;
        return;
      }
      if (e.button !== 0 || draggable || selectionMode !== "multiple") return;
      if (shouldIgnoreMarqueeStart(e.target)) return;

      const pos = { x: e.clientX, y: e.clientY };
      selectionStartPosRef.current = pos;
      setSelectionStartPos(pos);
      setSelectionCurrentPos(pos);

      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        isSelectingRef.current = true;
        setIsSelecting(true);
      }, 200);
    },
    [disabled, draggable, selectionMode, shouldIgnoreMarqueeStart]
  );

  useEffect(() => {
    const onDocumentMouseUp = () => {
      isLeftMouseDownRef.current = false;
      dragSelectStartKeyRef.current = null;
      dragSelectModeRef.current = "add";
      keyMouseDownRef.current = false;
    };
    document.addEventListener("mouseup", onDocumentMouseUp);
    return () => document.removeEventListener("mouseup", onDocumentMouseUp);
  }, []);

  useEffect(() => {
    if (!selectionStartPos) return;

    const onDocumentMouseMove = (e: MouseEvent) => {
      if (longPressTimerRef.current && e.buttons !== 1) {
        cancelMarqueeTracking();
        return;
      }

      if (e.buttons === 1 && isSelectingRef.current) {
        e.preventDefault();
        updateMarqueeFromClientPoint(e.clientX, e.clientY);
      }
    };

    const onDocumentMouseUp = () => {
      finishMarquee();
    };

    document.addEventListener("mousemove", onDocumentMouseMove);
    document.addEventListener("mouseup", onDocumentMouseUp);
    return () => {
      document.removeEventListener("mousemove", onDocumentMouseMove);
      document.removeEventListener("mouseup", onDocumentMouseUp);
    };
  }, [
    selectionStartPos,
    cancelMarqueeTracking,
    updateMarqueeFromClientPoint,
    finishMarquee,
  ]);

  useLayoutEffect(() => {
    if (disabled || draggable || selectionMode !== "multiple") return;

    let disposed = false;
    let removeListener: (() => void) | undefined;

    const attach = () => {
      if (disposed || removeListener) return;
      const el = getSelectionSurface();
      if (!el) return;

      const onSurfaceMouseDown = (e: MouseEvent) => {
        tryStartMarquee(e);
      };

      el.addEventListener("mousedown", onSurfaceMouseDown);
      removeListener = () => {
        el.removeEventListener("mousedown", onSurfaceMouseDown);
      };
    };

    attach();
    const raf = requestAnimationFrame(attach);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      removeListener?.();
    };
  }, [
    disabled,
    draggable,
    selectionMode,
    selectionHostRef,
    layoutLoading,
    activeLayout.length,
    getSelectionSurface,
    tryStartMarquee,
  ]);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // 渲染单个按键
  const renderKey = (
    key: KeyConfig,
    index: number,
    randomValue: any,
    singleTeampresetData: any,
    axisBody: any,
    rowIndex: number,
    axisData: any
  ) => {
    if (isGap(key)) {
      // return <></>;
      return null;
    }

    const state = keyStates[key.code] ||
      keyStates[key.uuid] || { pressed: false, selected: false };
    const isSelected = internalSelected.includes(key.uuid);
    const keySplitColors = getKeySplitColors(key, rowIndex);
    const customColor = keySplitColors ? undefined : getKeyColor(key, rowIndex);
    const showCustomColorCornerIcon =
      lightingMode === "custom" &&
      Boolean(
        customColorCornerIconSrc &&
        customColorCornerIconUuids?.includes(key.uuid)
      );
    const advancedKeyMode = resolveAdvancedKeyModeForKey(key, advancedKeyModes);
    const keepRealtimeColorWhenSelected =
      selectedKeysKeepRealtimeColor.includes(key.uuid);
    const isKeyDisabled =
      disabled || (disabledKeyCodes?.includes(key.keyCode ?? 0) ?? false);

    const isAxisLinkHighlighted =
      axisLinkHoverId != null &&
      axisDataMatchesAxisLink != null &&
      axisData != null &&
      axisDataMatchesAxisLink(axisData, axisLinkHoverId);

    const getSelectedStyle = () => {
      if (showRandomValues && randomValue?.status !== undefined) {
        const statusValue =
          typeof randomValue.status === "string"
            ? parseInt(randomValue.status, 10)
            : randomValue.status;
        /** Tank68 HE：对齐星闪——仅已校准着色，未校准保持默认键帽 */
        if (keyboardType === "tank68he") {
          const routeActive = isTank68HeCalibrationRouteActive(
            randomValue.route
          );
          if (statusValue === 1 || routeActive) {
            return {
              backgroundColor: "#f6532a",
              border: "2px solid #f6532a",
              color: "#1E2023",
            };
          }
          if (statusValue === 2) {
            return {
              backgroundColor: "rgba(255, 211, 197, 0.75)",
              color: "#1E2023",
              border: "none",
            };
          }
          return {};
        }
        if (statusValue === 2) {
          return {
            backgroundColor: "rgba(255, 211, 197, 0.75)",
            color: "#1E2023",
            border: "none",
          };
        }
        return {};
      }

      return isSelected ? selectedKeyStyle : {};
    };

    let totalWidth = 0;
    for (let i = 0; i < index; i++) {
      const previousKey = displayLayout[rowIndex][i];
      if (previousKey) {
        totalWidth += previousKey.multipleWidth * keySize;
        if (!isGap(previousKey)) {
          totalWidth += keyGap;
        }
      }
    }

    const leftPosition = `${totalWidth + containerPadding}px`;

    return (
      <div
        key={key.uuid}
        style={{
          position: "absolute",
          left: leftPosition,
          // 【修改】按键的上边距需要加上容器的内边距
          top: `${rowIndex * (keySize + keyGap) + containerPadding}px`,
        }}
      >
        <Key
          ref={(el) => {
            if (el) {
              keyRefs.current.set(key.uuid, el);
            } else {
              keyRefs.current.delete(key.uuid);
            }
          }}
          config={key}
          keySize={keySize}
          state={state}
          isSelected={isSelected}
          customColor={customColor}
          customColorTop={keySplitColors?.top}
          customColorBottom={keySplitColors?.bottom}
          doubleLightingSplit={doubleLightingKeySplit}
          doubleLightingSplitLayer={doubleLightingSplitLayer}
          styleMode={styleMode}
          lightingMode={lightingMode}
          showLabels={showLabels}
          isExtraKeys={isExtraKeys}
          extraKeyTooltipFormat={extraKeyTooltipFormat}
          advancedKeyMode={advancedKeyMode}
          onClick={handleKeyClick}
          onMouseDownSelect={handleKeyMouseDownSelect}
          onMouseEnterSelect={handleKeyMouseEnterSelect}
          onContextMenu={onKeyContextMenu ? handleKeyContextMenu : undefined}
          onDoubleClick={onKeyDoubleClick ? handleKeyDoubleClick : undefined}
          onDrop={handleKeyDrop}
          disabled={isKeyDisabled}
          draggable={draggable && !isKeyDisabled}
          pressedStyle={state.pressed ? pressedKeyStyle : {}}
          selectedStyle={getSelectedStyle()}
          selectedKeyBadge={selectedKeyBadge}
          selectedKeyTooltipText={
            isSelected ? selectedKeyTooltipText : undefined
          }
          selectedKeyStyleMap={selectedKeyStyleMap}
          keepRealtimeColorWhenSelected={keepRealtimeColorWhenSelected}
          customColorCornerIconSrc={customColorCornerIconSrc}
          showCustomColorCornerIcon={showCustomColorCornerIcon}
          suppressDefaultSelectedChrome={
            suppressSelectedKeyStyle ||
            (lightingMode === "custom" && isSelected)
          }
          allowIconKeySelectedStyle={allowIconKeySelectedStyle}
          randomAdcValue={randomValue.adc}
          randomRouteValue={randomValue.route}
          randomStatusValue={randomValue.status}
          axisBody={axisBody}
          randomPerformanceValue={randomValue.performance}
          showRandomValue={showRandomValues}
          hideCalibrationMetrics={hideCalibrationMetrics}
          showTeampreset={showTeampreset}
          teampresetData={singleTeampresetData}
          showAxis={showAxis}
          axisData={axisData}
          isAxisLinkHighlighted={isAxisLinkHighlighted}
          onAxisLinkHover={() => {
            if (!resolveAxisLinkIdFromData || !onAxisLinkHoverChange) return;
            const id = resolveAxisLinkIdFromData(axisData);
            if (id != null) onAxisLinkHoverChange(id);
          }}
          onAxisLinkHoverEnd={() => {
            onAxisLinkHoverChange?.(null);
          }}
        />
      </div>
    );
  };

  const emptyRandomValue = {
    performance: undefined,
    adc: undefined,
    route: undefined,
    status: undefined,
  };

  const isRandomValues = (
    rowIndex: number,
    key: KeyConfig,
    keyIndex: number
  ) => {
    if (isX75v3KeyboardType(keyboardType)) {
      const matrixRow = key.originalRow ?? rowIndex;
      const matrixCol = key.originalCol;
      if (matrixCol === undefined || matrixCol < 0) {
        return emptyRandomValue;
      }
      if (randomValues?.length) {
        const rowData = randomValues[matrixRow];
        if (!rowData) {
          return emptyRandomValue;
        }
        const sampleCol = resolveGamma75CalibrationSampleCol(
          matrixRow,
          matrixCol,
          keyIndex,
          rowData,
          keyboardType
        );
        if (
          Number(rowData.route?.[sampleCol]) > 3000 &&
          showRandomValues &&
          onGetPerformance
        ) {
          onGetPerformance(matrixRow, matrixCol);
        }
        return {
          performance: rowData.performance?.[sampleCol],
          adc: rowData.adc?.[sampleCol],
          route: rowData.route?.[sampleCol],
          status: rowData.status?.[sampleCol],
        };
      }
      return emptyRandomValue;
    }

    const dataCol = resolveCalibrationDataColIndex(key, keyIndex, keyboardType);
    const sdkCol = resolvePerformanceSdkColIndex(key, keyIndex, keyboardType);
    if (randomValues?.length) {
      const routeValue = randomValues[rowIndex].route?.[dataCol];
      const routeActive =
        keyboardType === "tank68he"
          ? isTank68HeCalibrationRouteActive(routeValue)
          : Number(routeValue) > 3000;
      if (routeActive && showRandomValues && onGetPerformance) {
        const sdkRow = usesSliceFirstRowLayout(keyboardType)
          ? resolveSdkMatrixRowFromVisual(rowIndex, keyboardType)
          : rowIndex;
        onGetPerformance(sdkRow, sdkCol);
      }
      return {
        performance: randomValues[rowIndex].performance?.[dataCol],
        adc: randomValues[rowIndex].adc?.[dataCol],
        route: randomValues[rowIndex].route?.[dataCol],
        status: randomValues[rowIndex].status?.[dataCol],
      };
    }
    return emptyRandomValue;
  };

  const resolvePresetAxisDataRow = (visualRowIndex: number) => {
    if (!usesSliceFirstRowLayout(keyboardType)) {
      return visualRowIndex;
    }
    return resolveLayoutDataRowIndex(
      visualRowIndex,
      activeLayout.length,
      teampresetData?.length ?? axisData?.length ?? 0
    );
  };

  const getSingleTeampresetData = (
    rowIndex: number,
    key: KeyConfig,
    keyIndex: number
  ) => {
    if (isX75v3KeyboardType(keyboardType)) {
      const inlinePerformance = (key as KeyConfig & { performance?: unknown })
        .performance;
      if (inlinePerformance != null) {
        return inlinePerformance;
      }
      if (!teampresetData.length || !teampresetData[rowIndex]) {
        return null;
      }
      const byIndex = teampresetData[rowIndex]?.[keyIndex]?.performance;
      if (byIndex != null) {
        return byIndex;
      }
      const match = teampresetData[rowIndex].find(
        (item: KeyConfig & { performance?: unknown }) => item.uuid === key.uuid
      );
      return match?.performance ?? null;
    }
    const dataRow = resolvePresetAxisDataRow(rowIndex);
    if (!teampresetData.length || !teampresetData[dataRow]) {
      return null;
    }
    if (usesSliceFirstRowLayout(keyboardType)) {
      const match = teampresetData[dataRow].find(
        (item: KeyConfig & { performance?: unknown }) => item.uuid === key.uuid
      );
      return match?.performance ?? null;
    }
    if (teampresetData[dataRow]?.[keyIndex]) {
      return teampresetData[dataRow][keyIndex]?.performance;
    }
    return null;
  };

  const getAxisBody = (rowIndex: number, key: KeyConfig, keyIndex: number) => {
    if (isX75v3KeyboardType(keyboardType)) {
      if (!teampresetData.length || !teampresetData[rowIndex]) {
        return null;
      }
      const match = teampresetData[rowIndex].find(
        (item: KeyConfig & { axisBody?: unknown; performance?: unknown }) =>
          item.uuid === key.uuid
      );
      if (!match) return null;
      const body = (match.axisBody ?? {}) as Record<string, unknown>;
      const perf = (match.performance ?? {}) as Record<string, unknown>;
      const axisRangeMax = Number(
        perf.axisRangeMax ?? body.axisRangeMax ?? body.axis_range_max ?? 0
      );
      const driveRange = Number(body.drive_range ?? 0);
      if (Number.isFinite(driveRange) && driveRange > 0) {
        return match.axisBody;
      }
      if (Number.isFinite(axisRangeMax) && axisRangeMax > 0) {
        return {
          ...body,
          ...perf,
          drive_range: axisRangeMax >= 100 ? axisRangeMax / 1000 : axisRangeMax,
        };
      }
      return match.axisBody ?? null;
    }
    const dataRow = resolvePresetAxisDataRow(rowIndex);
    if (teampresetData.length && teampresetData[dataRow]?.[keyIndex]) {
      return teampresetData[dataRow][keyIndex]?.axisBody;
    }
    return null;
  };

  const getAxisData = (rowIndex: number, key: KeyConfig, keyIndex: number) => {
    if (isX75v3KeyboardType(keyboardType)) {
      const matrixRow = key.originalRow ?? rowIndex;
      const matrixCol = key.originalCol;
      if (matrixCol === undefined || matrixCol < 0 || !axisData?.length) {
        return null;
      }
      return axisData[matrixRow]?.[matrixCol] ?? null;
    }
    const dataRow = resolvePresetAxisDataRow(rowIndex);
    if (axisData?.length && axisData[dataRow]?.[keyIndex]) {
      return axisData[dataRow][keyIndex];
    }
    return null;
  };

  const selectionBoxStyle: React.CSSProperties = useMemo(() => {
    if (!isSelecting || !selectionStartPos || !selectionCurrentPos) {
      return {};
    }

    const el = getSelectionSurface();
    if (!el) {
      return {};
    }

    const surfaceRect = el.getBoundingClientRect();
    /** ViewportFit 等父级 CSS scale 下，视觉坐标需换算为容器布局像素 */
    const scaleX = el.offsetWidth / surfaceRect.width || 1;
    const scaleY = el.offsetHeight / surfaceRect.height || 1;

    const clientXStart = selectionStartPos.x;
    const clientYStart = selectionStartPos.y;
    const clientXCurrent = selectionCurrentPos.x;
    const clientYCurrent = selectionCurrentPos.y;

    const left =
      (Math.min(clientXStart, clientXCurrent) - surfaceRect.left) * scaleX;
    const top =
      (Math.min(clientYStart, clientYCurrent) - surfaceRect.top) * scaleY;
    const width = Math.abs(clientXCurrent - clientXStart) * scaleX;
    const height = Math.abs(clientYCurrent - clientYStart) * scaleY;

    return { left, top, width, height };
  }, [
    isSelecting,
    selectionStartPos,
    selectionCurrentPos,
    getSelectionSurface,
  ]);

  const selectionSurfaceElement = getSelectionSurface();
  const selectionBox =
    !layoutLoading &&
    isSelecting &&
    selectionMode === "multiple" &&
    selectionSurfaceElement &&
    selectionBoxStyle.width != null &&
    selectionBoxStyle.height != null
      ? createPortal(
          <div className="selection-box" style={selectionBoxStyle} />,
          selectionSurfaceElement
        )
      : null;

  const keyboardVisual = layoutLoading ? (
    <KeyboardSkeleton
      keyboardType={keyboardType}
      keySize={keySize}
      keyGap={keyGap}
      styleMode={styleMode}
      className={className}
    />
  ) : (
    <div
      ref={keyboardRef}
      className={`shared-keyboard ${
        className || ""
      } shared-keyboard-type-${keyboardType} shared-keyboard-style-${styleMode}`}
      style={
        {
          width:
            keyboardDimensions.width > 0
              ? `${keyboardDimensions.width}px`
              : "auto",
          height:
            keyboardDimensions.height > 0
              ? `${keyboardDimensions.height}px`
              : undefined,
          "--key-size": `${keySize}px`,
          "--key-gap": `${keyGap}px`,
          /** 一律以实测宽度为准；勿对非 98 系列写死 1146px，否则 98→68 切换后占位仍为大键盘宽度 */
          "--keyboard-width":
            keyboardType === "98" ||
            keyboardType === "m80" ||
            keyboardType === "m71" ||
            keyboardType === "t87" ||
            keyboardType === "x75v2" ||
            keyboardType === "m75" ||
            keyboardDimensions.width > 0
              ? `${keyboardDimensions.width}px`
              : "auto",
          borderRadius: "12px",
        } as React.CSSProperties
      }
    >
      {displayLayout.map((row, rowIndex) => (
        <div key={rowIndex} className="shared-keyboard-row">
          {row.map((key, keyIndex) =>
            renderKey(
              key,
              keyIndex,
              isRandomValues(rowIndex, key, keyIndex),
              getSingleTeampresetData(rowIndex, key, keyIndex),
              getAxisBody(rowIndex, key, keyIndex),
              rowIndex,
              getAxisData(rowIndex, key, keyIndex)
            )
          )}
        </div>
      ))}
    </div>
  );

  const keyboardBody = (
    <>
      <div className="shared-keyboard-box">
        <div ref={selectionRootRef} className="shared-keyboard-selection-root">
          {disableViewportFit ? (
            keyboardVisual
          ) : (
            <ViewportFit className="shared-keyboard-selection-fit">
              {keyboardVisual}
            </ViewportFit>
          )}
        </div>
      </div>
      {selectionBox}
    </>
  );

  return keyboardBody;
};
